import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import { toastUtil } from '../../utils/toast'
import { useAuthStore } from '../../stores/authStore'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

if (!API_BASE_URL) {
  throw new Error(
    'REACT_APP_API_BASE_URL 환경 변수가 설정되지 않았습니다. .env 파일에 REACT_APP_API_BASE_URL을 추가해주세요.'
  )
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 60000, // 리뷰 저장 등 사진 처리로 시간이 오래 걸릴 수 있으므로 60초로 설정
})

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 앱 버전을 실어 보낸다.
    //
    // 앱이 웹뷰에 자기 버전을 넣어준다(webview_page.dart). 서버는 이 헤더로만
    // 사용자가 어느 버전을 쓰는지 알 수 있다 — 웹뷰 User-Agent에는 OS 버전만
    // 있고 앱 버전이 없다.
    //
    // 브리지는 페이지 로드가 끝난 뒤에 주입되므로 맨 처음 몇 요청에는 값이
    // 없을 수 있다. 그래도 괜찮다 — 한 세션에 요청이 여러 번 나가고, 서버는
    // 값이 바뀔 때만 기록한다.
    const bridge = (window as any).FlutterBridge
    if (bridge?.appVersion && config.headers) {
      config.headers['X-App-Version'] = String(bridge.appVersion)
      if (bridge.appBuild) config.headers['X-App-Build'] = String(bridge.appBuild)
      if (bridge.platform) config.headers['X-App-Platform'] = String(bridge.platform)
    }

    // FormData인 경우 Content-Type을 제거하여 axios가 자동으로 multipart/form-data + boundary 설정하도록 함
    if (config.data instanceof FormData && config.headers) {
      // 기본 헤더의 application/json을 제거하면 axios가 FormData를 감지하고
      // 자동으로 multipart/form-data; boundary=... 형식으로 설정함
      delete config.headers['Content-Type']
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

/**
 * 서버 메시지를 토스트에 맞게 다듬는다.
 *
 * 서버는 문장을 마침표로 끝낸다(컨트롤러 메시지 2400여 개 중 대부분).
 * 반면 앱 토스트는 마침표 없이 쓴다 — "팔로잉이 해제되었습니다" 처럼.
 * 그대로 띄우면 서버에서 온 토스트만 점이 붙어 혼자 튄다.
 *
 * 문장 중간의 마침표는 건드리지 않고 끝의 것만 뗀다.
 * "차단한 사용자입니다. 차단을 해제하면 팔로우할 수 있습니다." 같은 두 문장짜리는
 * 앞 문장의 점을 유지한다.
 */
const toToastText = (message: string): string =>
  message.replace(/\s*\.\s*$/, '')

apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error: AxiosError) => {
    // 요청 취소(AbortController)는 에러가 아님 — 토스트 없이 조용히 무시.
    // (자동완성처럼 이전 요청을 취소하는 경우 "네트워크 오류" 오탐 방지)
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    // skipErrorHandler 옵션이 있으면 에러 메시지를 표시하지 않음
    const config = error.config as InternalAxiosRequestConfig & {
      skipErrorHandler?: boolean
    }

    if (error.response) {
      const status = error.response.status
      const data = error.response.data as any

      switch (status) {
        case 401:
          // 진단 요청 자체가 401을 받은 경우엔 아무것도 하지 않는다.
          // 여기서 또 진단을 보내면 401 → 진단 → 401 … 로 끝없이 돈다.
          // (이미 로그아웃된 뒤에 남은 요청이 도착한 상황이라 지울 것도 없다)
          if (config?.url?.includes('/diagnostics/')) {
            break
          }
          // 어떤 요청이 401을 냈는지 남긴다. clearAuth가 토큰을 지우기 전에
          // 부르므로, 이 요청 자체는 아직 인증된 상태로 나간다.
          import('../../utils/authDiagnostics').then((m) =>
            m.reportAuthLost('http_401', `${config?.method} ${config?.url}`)
          )
          // 스토어와 localStorage 모두 초기화
          useAuthStore.getState().clearAuth()
          // 인증이 필요한 페이지에서 401 발생 시 로그인 페이지로 리다이렉트
          // Splash 페이지(/)에서는 자체적으로 인증 상태를 확인하므로 리다이렉트하지 않음
          if (
            window.location.pathname !== '/' &&
            window.location.pathname !== '/login' &&
            window.location.pathname !== '/register' &&
            !window.location.pathname.startsWith('/admin')
          ) {
            window.location.href = '/login'
          }
          break
        case 422:
          // 유효성 검증 실패 시 에러 메시지 표시
          if (!config?.skipErrorHandler) {
            if (data?.errors) {
              // 각 필드별 에러 메시지가 있는 경우 첫 번째 메시지 표시
              const errorMessages = Object.values(
                data.errors
              ).flat() as string[]
              if (errorMessages.length > 0) {
                toastUtil.error(toToastText(errorMessages[0]))
              } else if (data?.message) {
                toastUtil.error(toToastText(data.message))
              } else {
                toastUtil.error('입력 정보를 확인해주세요')
              }
            } else if (data?.message) {
              toastUtil.error(toToastText(data.message))
            } else {
              toastUtil.error('입력 정보를 확인해주세요')
            }
          }
          break
        case 404:
          // skipErrorHandler가 true이면 에러 메시지를 표시하지 않음
          if (!config?.skipErrorHandler) {
            if (data?.message) {
              toastUtil.error(toToastText(data.message))
            } else {
              toastUtil.error('리소스를 찾을 수 없습니다')
            }
          }
          break
        case 500:
          toastUtil.error('서버 오류가 발생했습니다')
          break
        default:
          if (!config?.skipErrorHandler) {
            if (data?.message) {
              toastUtil.error(toToastText(data.message))
            } else {
              toastUtil.error('오류가 발생했습니다')
            }
          }
      }
    } else if (error.request) {
      if (!config?.skipErrorHandler) {
        toastUtil.error('네트워크 오류가 발생했습니다')
      }
    } else {
      if (!config?.skipErrorHandler) {
        toastUtil.error('요청 처리 중 오류가 발생했습니다')
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
